import { useEffect, useRef } from "react";
import {
  CELL_SIZE,
  GRID_COLUMNS,
  GRID_ROWS,
  canvasCellSize,
  cellDeltaFromPixels,
  findCard,
  gridColumnLabels,
  gridCssVars,
  gridCellCount,
  gridRowLabels,
  nudgeCardsCells,
  tryMoveCard,
  tryMoveCardsTo,
  tryResizeCard,
  tryStyleCard,
} from "@/lib/pageDesignerLayout";
import { PD } from "@/constants/testIds";
import { isRtfCard } from "@/lib/pageDesignerRtf";
import { isSearchCard } from "@/lib/pageDesignerSearch";
import { isTableCard } from "@/lib/pageDesignerTable";
import PageDesignerRtfEditor, {
  PageDesignerRtfPreview,
} from "@/components/pageDesigner/PageDesignerRtfEditor";
import PageDesignerSearchContent from "@/components/pageDesigner/PageDesignerSearchContent";
import PageDesignerTableContent from "@/components/pageDesigner/PageDesignerTableContent";

/**
 * A card's position/size as a percentage of the page grid. Purely derived
 * from col/row/width/height — there is no per-card pixel offset to add.
 */
function cardStyle(card, selected, rtf) {
  const inset = 3;
  return {
    left: `calc((${card.col} - 1) * ${100 / GRID_COLUMNS}% + ${inset}px)`,
    top: `calc((${card.row} - 1) * ${100 / GRID_ROWS}% + ${inset}px)`,
    width: `calc(${card.width} * ${100 / GRID_COLUMNS}% - ${inset * 2}px)`,
    height: `calc(${card.height} * ${100 / GRID_ROWS}% - ${inset * 2}px)`,
    background: card.background,
    borderColor: card.border,
    color: rtf ? "#212121" : card.border,
    outline: selected ? `1px solid ${card.border}` : "none",
  };
}

const ARROW_KEYS = {
  ArrowUp: { dx: 0, dy: -1 },
  ArrowDown: { dx: 0, dy: 1 },
  ArrowLeft: { dx: -1, dy: 0 },
  ArrowRight: { dx: 1, dy: 0 },
};
/** Arrow keys move by whole cells; Shift takes a coarse 10-cell stride. */
const ARROW_STEP = 1;
const ARROW_STEP_SHIFT = 10;

/** True for text inputs, selects, and contentEditable areas — arrow keys must not be hijacked there. */
function isEditableTarget(target) {
  if (!target) return false;
  const tag = typeof target.tagName === "string" ? target.tagName.toUpperCase() : "";
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return Boolean(target.isContentEditable);
}

function DragDots() {
  return Array.from({ length: 6 }, (_, index) => (
    <span key={index} className="epd__drag-dot" aria-hidden="true" />
  ));
}

export default function PageDesignerCanvas({
  cards,
  selectedIds = [],
  disabled,
  preview = false,
  canvasRef,
  onSelect,
  onCardsChange,
}) {
  const colLabels = gridColumnLabels();
  const rowLabels = gridRowLabels();
  const cellCount = gridCellCount();
  const cssVars = gridCssVars();
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const selectRef = useRef(onSelect);
  const changeRef = useRef(onCardsChange);
  selectRef.current = onSelect;
  changeRef.current = onCardsChange;

  const measure = () => canvasCellSize(canvasRef.current?.getBoundingClientRect());

  useEffect(() => {
    const onMove = (event) => {
      const drag = dragRef.current;
      if (drag && drag.pointerId === event.pointerId) {
        // A drag always lands on whole cells: the pointer delta is read as
        // a cell delta, so there is no sub-cell position to keep anywhere.
        const delta = cellDeltaFromPixels(
          event.clientX - drag.startX,
          event.clientY - drag.startY,
          drag.cellWidth,
          drag.cellHeight,
        );
        if (drag.positions.length > 1) {
          const moves = drag.positions.map((position) => ({
            id: position.id,
            col: position.startCol + delta.col,
            row: position.startRow + delta.row,
          }));
          changeRef.current((current) => tryMoveCardsTo(current, moves) || current);
        } else {
          const position = drag.positions[0];
          changeRef.current((current) => {
            const next = tryMoveCard(
              current,
              position.id,
              position.startCol + delta.col,
              position.startRow + delta.row,
            );
            return next || current;
          });
        }
        return;
      }
      const resize = resizeRef.current;
      if (resize && resize.pointerId === event.pointerId) {
        const delta = cellDeltaFromPixels(
          event.clientX - resize.startX,
          event.clientY - resize.startY,
          resize.cellWidth,
          resize.cellHeight,
        );
        changeRef.current((current) => {
          const next = tryResizeCard(
            current,
            resize.id,
            resize.startWidth + delta.col,
            resize.startHeight + delta.row,
          );
          return next || current;
        });
      }
    };
    const onUp = (event) => {
      if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
      if (resizeRef.current?.pointerId === event.pointerId) resizeRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [canvasRef]);

  useEffect(() => {
    if (disabled || preview || selectedIds.length === 0) return undefined;
    const onKeyDown = (event) => {
      const arrow = ARROW_KEYS[event.key];
      if (!arrow) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      const step = event.shiftKey ? ARROW_STEP_SHIFT : ARROW_STEP;
      changeRef.current((current) => {
        const next = nudgeCardsCells(current, selectedIds, arrow.dx * step, arrow.dy * step);
        return next || current;
      });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [disabled, preview, selectedIds]);

  const selectCardOnly = (id, options) => {
    selectRef.current(id, options);
  };

  const onDragPointerDown = (event, card) => {
    if (disabled || preview) return;
    event.preventDefault();
    event.stopPropagation();
    // Dragging a card that's already part of a multi-selection moves the
    // whole group and keeps the selection; otherwise it behaves like a
    // plain click, collapsing the selection to just this card.
    const groupIds = selectedIds.length > 1 && selectedIds.includes(card.id) ? selectedIds : [card.id];
    if (groupIds.length === 1) selectCardOnly(card.id);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const { cellWidth, cellHeight } = measure();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      cellWidth,
      cellHeight,
      // A selected id can outlive its card; drag only the ones still there.
      positions: groupIds
        .map((id) => (id === card.id ? card : findCard(cards, id)))
        .filter(Boolean)
        .map((groupCard) => ({
          id: groupCard.id,
          startCol: groupCard.col,
          startRow: groupCard.row,
        })),
    };
  };

  const onResizePointerDown = (event, card) => {
    if (disabled || preview) return;
    event.preventDefault();
    event.stopPropagation();
    selectCardOnly(card.id);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const { cellWidth, cellHeight } = measure();
    resizeRef.current = {
      id: card.id,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: card.width,
      startHeight: card.height,
      cellWidth,
      cellHeight,
    };
  };

  return (
    <div
      className={`epd__stage${preview ? " epd__stage--preview" : ""}`}
      style={cssVars}
    >
      {preview ? null : (
        <>
          <div className="epd__corner" />
          <div
            className="epd__col-axis"
            data-testid={PD.colAxis}
            aria-hidden="true"
            style={{ gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))` }}
          >
            {colLabels.map((label) => (
              <span key={`col-${label}`}>{label}</span>
            ))}
          </div>
          <div
            className="epd__row-axis"
            data-testid={PD.rowAxis}
            aria-hidden="true"
            style={{ gridTemplateRows: `repeat(${GRID_ROWS}, minmax(0, 1fr))` }}
          >
            {rowLabels.map((label) => (
              <span key={`row-${label}`}>{label}</span>
            ))}
          </div>
        </>
      )}
      <div
        ref={canvasRef}
        className={`epd__canvas${preview ? " epd__canvas--preview" : ""}`}
        data-testid={PD.canvas}
        data-columns={GRID_COLUMNS}
        data-rows={GRID_ROWS}
        data-cell-size={CELL_SIZE}
        data-preview={preview ? "true" : "false"}
        style={{
          ...cssVars,
          aspectRatio: `${GRID_COLUMNS} / ${GRID_ROWS}`,
          gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${GRID_ROWS}, minmax(0, 1fr))`,
        }}
        onPointerDown={() => {
          if (preview) return;
          onSelect(null);
        }}
      >
        {preview
          ? null
          : Array.from({ length: cellCount }, (_, index) => (
              <div
                key={index}
                className="epd__cell"
                data-testid={PD.cell}
                data-col={(index % GRID_COLUMNS) + 1}
                data-row={Math.floor(index / GRID_COLUMNS) + 1}
              />
            ))}
        {cards.map((card) => {
          const selected = !preview && selectedIds.includes(card.id);
          const rtf = isRtfCard(card);
          const table = isTableCard(card);
          const search = isSearchCard(card);
          const contentCard = rtf || table || search;
          return (
            <div
              key={card.id}
              role={preview || table || search ? "article" : rtf ? "group" : "button"}
              tabIndex={preview || contentCard ? undefined : 0}
              className={`epd__card${selected ? " is-selected" : ""}${rtf ? " epd__card--rtf" : ""}${table ? " epd__card--table" : ""}${search ? " epd__card--search" : ""}`}
              data-testid={`${PD.card}-${card.id}`}
              data-card-id={card.id}
              data-col={card.col}
              data-row={card.row}
              data-width={card.width}
              data-height={card.height}
              data-content-type={card.contentType || "blank"}
              data-behavior={card.behavior || "none"}
              style={cardStyle(card, selected, contentCard)}
              aria-label={`${card.title}, column ${card.col}, row ${card.row}, ${card.width} by ${card.height}`}
              aria-pressed={preview || contentCard ? undefined : selected}
              onPointerDown={(event) => {
                event.stopPropagation();
                if (preview) return;
                const additive = event.shiftKey || event.ctrlKey || event.metaKey;
                selectCardOnly(card.id, { additive });
              }}
            >
              {preview ? null : (
                <button
                  type="button"
                  className="epd__drag-handle"
                  data-testid={`${PD.dragHandle}-${card.id}`}
                  aria-label={`Drag ${card.title}`}
                  disabled={disabled}
                  onPointerDown={(event) => onDragPointerDown(event, card)}
                >
                  <DragDots />
                </button>
              )}
              {rtf ? (
                preview ? (
                  <PageDesignerRtfPreview
                    html={card.rtfHtml}
                    testId={`${PD.rtfPreview}-${card.id}`}
                  />
                ) : (
                  <PageDesignerRtfEditor
                    cardId={card.id}
                    html={card.rtfHtml || ""}
                    disabled={disabled}
                    onChange={(rtfHtml) => {
                      changeRef.current((current) =>
                        tryStyleCard(current, card.id, { rtfHtml }) || current,
                      );
                    }}
                  />
                )
              ) : table ? (
                <PageDesignerTableContent card={card} preview={preview} />
              ) : search ? (
                <PageDesignerSearchContent card={card} preview={preview} />
              ) : (
                <span className="epd__card-title">{card.title}</span>
              )}
              {preview ? null : (
                <button
                  type="button"
                  className="epd__resize-handle"
                  data-testid={`${PD.resizeHandle}-${card.id}`}
                  aria-label={`Resize ${card.title}`}
                  disabled={disabled}
                  onPointerDown={(event) => onResizePointerDown(event, card)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
